import { useEffect, useState } from 'react';
import ReactTable from 'react-table';
import Toggle from 'react-toggle';
import type { VoidFunctionComponent } from 'react';

import { flagDescriptions, FlagType, flagTypeDescriptions } from '../../../../shared/featureFlags/flagNames';
import { getFeaturesForStaticPlan } from '../../../../shared/subscriptions/utils';
import { useOnConfirm } from '../../../contexts/DialogContext';
import { window } from '../../../globals';
import { useGetAllFeatureFlags } from '../../../hooks/api/featureFlags/useGetAllFeatureFlags';
import { useToggleFeatureFlag } from '../../../hooks/api/featureFlags/useToggleFeatureFlag';
import Accordion from '../../generic/Accordion';
import TooltipBadge from '../../generic/Badge/TooltipBadge';
import Button from '../../generic/Button/Button';
import { BUTTON_SIZE, BUTTON_TYPE } from '../../generic/Button/button-constants';
import Card from '../../generic/Card/Card';
import Flex from '../../generic/Flex';
import SearchInput from '../../generic/Forms/SearchInput';
import Tooltip from '../../generic/Tooltip';
import type { FlagName, VisibleFlagName } from '../../../../shared/featureFlags/types';

import { type OrgFlag, problematicFlagMessage, sortFlags } from './utils';

type FeatureControlCardProps = {
  setError(err: unknown): void;
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
};

// Flag colors are used to color the badges in the table
const FlagColors = {
  [FlagType.Beta]: 'blue',
  [FlagType.Deprecated]: 'red',
  [FlagType.Alpha]: 'yellow',
  [FlagType.Product]: 'green',
  [FlagType.Configuration]: 'dark-gray',
  [FlagType.Tweaks]: 'dark-gray',
  [FlagType.Customer]: 'light-red',
  [FlagType.Performance]: 'light-green',
  [FlagType.Internal]: 'purple',
} as const;

const FeatureControlCard: VoidFunctionComponent<FeatureControlCardProps> = ({
  setError,
  isOpen,
  onToggle,
}) => {
  const [isSectionOpen, setIsSectionOpen] = useState(isOpen ?? false);
  const handleToggle = () => {
    setIsSectionOpen(!isSectionOpen);
    onToggle?.(isSectionOpen);
  };

  const [flags, setFlags] = useState<OrgFlag[]>([]);
  const [flagSearch, setFlagSearch] = useState<string>('');
  const [flagsToTurnOn, setFlagsToTurnOn] = useState<VisibleFlagName[]>([]);
  const [flagsToTurnOff, setFlagsToTurnOff] = useState<VisibleFlagName[]>([]);
  const { data: availableFlags } = useGetAllFeatureFlags();
  const { mutateAsync: toggleFlag, isPending } = useToggleFeatureFlag();
  const tierFeatures = getFeaturesForStaticPlan(window.user?.organization?.stripe_plan);
  const onConfirm = useOnConfirm();

  // set flags based on results from availableFlags
  useEffect(() => {
    const orgFlags = window.enabledFeatureFlags;
    const currentOrgFlags: OrgFlag[] = Object.entries(availableFlags?.flags ?? {})
      .map(([flag, { displayName, description, type }]) => ({
        id: flag,
        displayName,
        description,
        type,
        isEnabled: orgFlags.some((orgFlag) => orgFlag.flag === flag),
      }))
      .sort(sortFlags);

    setFlags(currentOrgFlags);
  }, [availableFlags?.flags]);

  // Data for ReactTable - matches against flagSearch and tierFeatures
  const flagData = flags.filter((f) => {
    if (!flagSearch) {
      return true;
    }

    const includesFlagSearch = (value: string) => value.toLowerCase().includes(flagSearch.toLowerCase());
    return [f.displayName, f.id, f.description, f.type].some(includesFlagSearch);
  }).filter(({ id }) => !tierFeatures.has(id as FlagName));

  // flag toggle method for featureFlagColumns
  const toggleFlagLocal = (flag: VisibleFlagName, isEnabled: boolean) => {
    try {
      const tempFlagsToTurnOff = flagsToTurnOff;
      const tempFlagsToTurnOn = flagsToTurnOn;
      const offIndex = tempFlagsToTurnOff.indexOf(flag);
      const onIndex = tempFlagsToTurnOn.indexOf(flag);
      if (offIndex > -1) {
        tempFlagsToTurnOff.splice(offIndex, 1);
      } else if (onIndex > -1) {
        tempFlagsToTurnOn.splice(onIndex, 1);
      } else if (isEnabled) {
        tempFlagsToTurnOn.push(flag);
      } else {
        tempFlagsToTurnOff.push(flag);
      }

      // oxlint-disable-next-line oxc/no-map-spread -- copy on purpose: `ff` is React state
      const tempFlags = flags.map((ff) => (ff.id === flag ? { ...ff, isEnabled } : ff));
      setFlags(tempFlags);
      setFlagsToTurnOff(tempFlagsToTurnOff);
      setFlagsToTurnOn(tempFlagsToTurnOn);
    } catch (err) {
      setError(err);
    }
  };

  // Columns for ReactTable - includes toggleFlag method
  const featureFlagColumns = [
    {
      id: 'id',
      accessor: 'id',
      show: false,
    },
    {
      id: 'displayName',
      accessor: 'displayName',
      Header: 'Flag Name',
      sortable: true,
      width: 200,
      // eslint-disable-next-line react/no-unstable-nested-components
      Cell: ({ original }: { original: OrgFlag }) => (
        <Tooltip placement="top" tooltip={original.id} id={original.id}>
          <p>{original.displayName}</p>
        </Tooltip>
      ),
    },
    {
      id: 'description',
      accessor: 'description',
      Header: 'Description',
      style: { whiteSpace: 'unset' }, // Do not trim description for flags, wrap text instead.
    },
    {
      id: 'type',
      accessor: 'type',
      Header: 'Type',
      width: 100,
      // eslint-disable-next-line react/no-unstable-nested-components
      Cell: ({ original }: { original: OrgFlag }) => {
        const { id, type } = original;
        return (
          <TooltipBadge
            color={FlagColors?.[type] || 'light-gray'}
            shape="circle"
            id={id}
            tooltip={flagTypeDescriptions[type]}
          >
            {type}
          </TooltipBadge>
        );
      },
    },
    {
      id: 'enabled',
      accessor: 'enabled',
      Header: '',
      width: 60,
      // eslint-disable-next-line react/no-unstable-nested-components
      Cell: ({ original }: { original: OrgFlag }) => {
        const { id, isEnabled } = original;
        return <Toggle checked={isEnabled} onChange={() => toggleFlagLocal(id, !isEnabled)} icons={false} />;
      },
    },
  ];

  // Handle clicks, allow us to show alerts/block bad or destructive actions.
  const onSaveFlagsClick = () => {
    const saveFlags = async () => {
      try {
        // Turn off flags sequentially to update window state correctly
        await flagsToTurnOff.reduce(async (prevPromise, flag) => {
          await prevPromise;
          await toggleFlag({ flag: flag as FlagName, enable: false });
          window.enabledFeatureFlags = window.enabledFeatureFlags.filter((ff) => ff.flag !== flag);
        }, Promise.resolve());

        // Turn on flags sequentially to update window state correctly
        await flagsToTurnOn.reduce(async (prevPromise, flag) => {
          await prevPromise;
          await toggleFlag({ flag: flag as FlagName, enable: true });

          if (!window.user?.organizationId) {
            throw new Error('No organizationId found for this user');
          }

          window.enabledFeatureFlags.push({
            flag: flag as FlagName,
            organizationId: window.user.organizationId,
          });
        }, Promise.resolve());

        // Reload the page to reflect changes
        window.location.reload();
      } catch (err) {
        setError(err);
      }
    };

    if (!flagsToTurnOff.length && !flagsToTurnOn.length) {
      return;
    }

    const alphaFlagsToTurnOn = flagsToTurnOn.filter(
      (flag: VisibleFlagName) => flagDescriptions[flag]?.type === FlagType.Alpha,
    );

    const deprecatedFlagsToTurnOn = flagsToTurnOn.filter(
      (flag: VisibleFlagName) => flagDescriptions[flag]?.type === FlagType.Deprecated,
    );

    const areThereAlphaFlagsToTurnOn = alphaFlagsToTurnOn.length > 0;
    const areThereDeprecatedFlagsToTurnOn = deprecatedFlagsToTurnOn.length > 0;

    if (areThereAlphaFlagsToTurnOn || areThereDeprecatedFlagsToTurnOn) {
      // Build message based on which flags are being enabled
      const message = problematicFlagMessage(areThereAlphaFlagsToTurnOn, areThereDeprecatedFlagsToTurnOn);

      // Build list of flags + types for problem types
      const problematicFlagNames = [...alphaFlagsToTurnOn, ...deprecatedFlagsToTurnOn].map(
        (flag) => [flagDescriptions[flag]!.displayName, flagDescriptions[flag]!.type],
      );

      const body = (
        <>
          {message} <b>Please contact Engineering before enabling these flags.</b>
          <br />
          <br />
          <ul>
            {problematicFlagNames.map(([displayName, type]) => (
              <li key={displayName}>
                {displayName} ({type})
              </li>
            ))}
          </ul>
        </>
      );
      onConfirm({
        title: '🤠 Hold up!',
        body,
        onConfirm: saveFlags,
        confirmText: 'I understand the risk',
        confirmBtnType: BUTTON_TYPE.ERROR,
      });
      return;
    }

    saveFlags();
  };

  // Save button only enabled when there are flags to change
  const isSaveDisabled = (!flagsToTurnOff.length && !flagsToTurnOn.length) || isPending;

  return (
    <Card isFullWidth padding="medium">
      <Card.Content>
        <Accordion id="feature-control-card" isInitiallyOpen={isSectionOpen}>
          <Accordion.Target onClick={handleToggle}>Organization Feature Control</Accordion.Target>
          <Accordion.Body>
            <Flex justify="space-between" gap={10}>
              <SearchInput
                placeholder="Search for flag..."
                label="Search for feature flags"
                value={flagSearch}
                onChange={setFlagSearch}
            />
              <Button
                size={BUTTON_SIZE.LARGE}
                type={BUTTON_TYPE.PRIMARY}
                isDisabled={isSaveDisabled}
                onClick={onSaveFlagsClick}
            >
                Save Flags
              </Button>
            </Flex>
            <ReactTable minRows={0} columns={featureFlagColumns} data={flagData} defaultPageSize={50} />
          </Accordion.Body>
        </Accordion>
      </Card.Content>
    </Card>
  );
};

export default FeatureControlCard;
